import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import paidAdsService from '../services/paidAdsService';

/**
 * Paid Advertising Dashboard
 * 
 * Comprehensive paid ads management including:
 * - Google Ads campaigns
 * - Meta (Facebook/Instagram) Ads campaigns
 * - Performance optimization
 * - Bid automation
 * - Budget management
 */

const PLATFORMS = [
  { id: 'google', name: 'Google Ads', icon: '🔍', color: '#4285f4' },
  { id: 'meta', name: 'Meta Ads', icon: '📘', color: '#1877f2' },
  { id: 'linkedin', name: 'LinkedIn Ads', icon: '💼', color: '#0a66c2' },
  { id: 'tiktok', name: 'TikTok Ads', icon: '🎵', color: '#000000' }
];

const CAMPAIGN_TYPES = {
  google: [
    { id: 'search', name: 'Search', icon: '🔎' },
    { id: 'display', name: 'Display', icon: '🖼️' },
    { id: 'shopping', name: 'Shopping', icon: '🛒' },
    { id: 'video', name: 'Video', icon: '📹' },
    { id: 'performance-max', name: 'Performance Max', icon: '🚀' }
  ],
  meta: [
    { id: 'awareness', name: 'Brand Awareness', icon: '👁️' },
    { id: 'traffic', name: 'Traffic', icon: '🔗' },
    { id: 'engagement', name: 'Engagement', icon: '💬' },
    { id: 'leads', name: 'Lead Generation', icon: '📋' },
    { id: 'conversions', name: 'Conversions', icon: '🎯' },
    { id: 'catalog-sales', name: 'Catalog Sales', icon: '🛍️' }
  ]
};

const BIDDING_STRATEGIES = [
  { id: 'manual-cpc', name: 'Manual CPC', description: 'Set your own maximum cost-per-click' },
  { id: 'maximize-clicks', name: 'Maximize Clicks', description: 'Get the most clicks within budget' },
  { id: 'maximize-conversions', name: 'Maximize Conversions', description: 'Get the most conversions' },
  { id: 'target-cpa', name: 'Target CPA', description: 'Target cost per acquisition' },
  { id: 'target-roas', name: 'Target ROAS', description: 'Target return on ad spend' }
];

const STATUS_COLORS = {
  active: '#10b981',
  paused: '#f59e0b',
  ended: '#6b7280',
  draft: '#3b82f6',
  rejected: '#ef4444'
};

const PaidAds = () => {
  const navigate = useNavigate();
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [activePlatform, setActivePlatform] = useState('google');
  const [campaigns, setCampaigns] = useState([]);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Form states
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    platform: 'google',
    type: 'search',
    objective: '',
    budget_daily: '',
    budget_total: '',
    start_date: '',
    end_date: '',
    bidding_strategy: 'maximize-conversions',
    target_cpa: '',
    target_roas: '',
    targeting: {
      locations: [],
      age_range: { min: 18, max: 65 },
      interests: [],
      keywords: []
    }
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusData, budgetDataResp] = await Promise.all([
        paidAdsService.getStatus().catch(() => ({ connected: false })),
        paidAdsService.getBudgetOverview().catch(() => null)
      ]);

      let campaignsData;
      if (activePlatform === 'google') {
        campaignsData = await paidAdsService.getGoogleCampaigns().catch(() => ({ campaigns: [] }));
      } else {
        campaignsData = await paidAdsService.getMetaCampaigns().catch(() => ({ campaigns: [] }));
      }

      setServiceStatus(statusData);
      setBudgetData(budgetDataResp);
      setCampaigns(campaignsData.campaigns || []);
    } catch (err) {
      console.error('Error fetching paid ads data:', err);
      setError('Failed to load paid advertising data');
    } finally {
      setLoading(false);
    }
  }, [activePlatform]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create campaign
  const handleCreateCampaign = async () => {
    setLoading(true);
    try {
      if (campaignForm.platform === 'google') {
        await paidAdsService.createGoogleCampaign(campaignForm);
      } else {
        await paidAdsService.createMetaCampaign(campaignForm);
      }
      setShowCampaignModal(false);
      setSuccess('Campaign created successfully');
      fetchData();
    } catch (err) {
      setError('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  // Optimize campaign
  const handleOptimize = async () => {
    if (!selectedCampaign) return;
    
    setLoading(true);
    try {
      await paidAdsService.optimizePerformance({
        campaign_id: selectedCampaign.id,
        platform: selectedCampaign.platform
      });
      setShowOptimizeModal(false);
      setSuccess('Optimization applied successfully');
      fetchData();
    } catch (err) {
      setError('Failed to optimize campaign');
    } finally {
      setLoading(false);
    }
  };

  // Setup bid automation
  const handleSetupBidAutomation = async () => {
    if (!selectedCampaign) return;
    
    setLoading(true);
    try {
      await paidAdsService.setupBidAutomation({
        campaign_id: selectedCampaign.id,
        strategy: campaignForm.bidding_strategy,
        target_cpa: campaignForm.target_cpa,
        target_roas: campaignForm.target_roas
      });
      setShowBidModal(false);
      setSuccess('Bid automation configured successfully');
      fetchData();
    } catch (err) {
      setError('Failed to setup bid automation');
    } finally {
      setLoading(false);
    }
  };

  // Pause/Resume campaign
  const handleToggleCampaign = async (campaign) => {
    setLoading(true);
    try {
      if (campaign.platform === 'google') {
        if (campaign.status === 'active') {
          await paidAdsService.pauseGoogleCampaign(campaign.id);
        } else {
          await paidAdsService.resumeGoogleCampaign(campaign.id);
        }
      } else {
        if (campaign.status === 'active') {
          await paidAdsService.pauseMetaCampaign(campaign.id);
        } else {
          await paidAdsService.resumeMetaCampaign(campaign.id);
        }
      }
      setSuccess(`Campaign ${campaign.status === 'active' ? 'paused' : 'resumed'}`);
      fetchData();
    } catch (err) {
      setError('Failed to update campaign status');
    } finally {
      setLoading(false);
    }
  };

  // Render stats cards
  const renderStatsCards = () => (
    <div className="stats-grid-4 mb-4">
      <div className="stat-card">
        <div className="stat-icon">💰</div>
        <div className="stat-content">
          <div className="stat-value">${budgetData?.total_spend?.toLocaleString() || '0'}</div>
          <div className="stat-label">Total Spend</div>
          <div className="stat-trend success">+12% vs last month</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">👆</div>
        <div className="stat-content">
          <div className="stat-value">{budgetData?.total_clicks?.toLocaleString() || '0'}</div>
          <div className="stat-label">Total Clicks</div>
          <div className="stat-trend success">+8% vs last month</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">🎯</div>
        <div className="stat-content">
          <div className="stat-value">{budgetData?.conversions?.toLocaleString() || '0'}</div>
          <div className="stat-label">Conversions</div>
          <div className="stat-trend success">+15% vs last month</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">📈</div>
        <div className="stat-content">
          <div className="stat-value">{budgetData?.roas || '0'}x</div>
          <div className="stat-label">ROAS</div>
          <div className="stat-trend success">+0.3x vs last month</div>
        </div>
      </div>
    </div>
  );

  // Render campaign card
  const renderCampaignCard = (campaign) => {
    const platform = PLATFORMS.find(p => p.id === campaign.platform) || PLATFORMS[0];
    
    return (
      <div key={campaign.id} className="campaign-card">
        <div className="campaign-header">
          <div className="campaign-platform" style={{ background: platform.color }}>
            {platform.icon}
          </div>
          <div className="campaign-info">
            <h4 className="campaign-name">{campaign.name}</h4>
            <span className="campaign-type">{campaign.type}</span>
          </div>
          <div 
            className="campaign-status"
            style={{ background: STATUS_COLORS[campaign.status] }}
          >
            {campaign.status}
          </div>
        </div>

        <div className="campaign-metrics">
          <div className="metric">
            <span className="metric-value">${campaign.spend?.toLocaleString() || '0'}</span>
            <span className="metric-label">Spend</span>
          </div>
          <div className="metric">
            <span className="metric-value">{campaign.impressions?.toLocaleString() || '0'}</span>
            <span className="metric-label">Impressions</span>
          </div>
          <div className="metric">
            <span className="metric-value">{campaign.clicks?.toLocaleString() || '0'}</span>
            <span className="metric-label">Clicks</span>
          </div>
          <div className="metric">
            <span className="metric-value">{campaign.ctr?.toFixed(2) || '0'}%</span>
            <span className="metric-label">CTR</span>
          </div>
          <div className="metric">
            <span className="metric-value">{campaign.conversions || '0'}</span>
            <span className="metric-label">Conversions</span>
          </div>
          <div className="metric">
            <span className="metric-value">${campaign.cpa?.toFixed(2) || '0'}</span>
            <span className="metric-label">CPA</span>
          </div>
        </div>

        <div className="campaign-budget">
          <div className="budget-progress">
            <div className="budget-info">
              <span>Budget: ${campaign.budget_spent?.toLocaleString() || '0'} / ${campaign.budget_total?.toLocaleString() || '0'}</span>
              <span>{campaign.budget_percentage || 0}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${campaign.budget_percentage || 0}%`,
                  background: campaign.budget_percentage > 90 ? '#ef4444' : '#10b981'
                }}
              />
            </div>
          </div>
        </div>

        <div className="campaign-actions">
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => handleToggleCampaign(campaign)}
          >
            {campaign.status === 'active' ? '⏸️ Pause' : '▶️ Resume'}
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setSelectedCampaign(campaign);
              setShowOptimizeModal(true);
            }}
          >
            🎯 Optimize
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setSelectedCampaign(campaign);
              setShowBidModal(true);
            }}
          >
            💰 Bidding
          </button>
          <button className="btn btn-sm btn-primary">
            📊 Analytics
          </button>
        </div>
      </div>
    );
  };

  // Render campaign creation modal
  const renderCampaignModal = () => (
    <div className="modal-overlay" onClick={() => setShowCampaignModal(false)}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create New Campaign</h3>
          <button className="btn-icon" onClick={() => setShowCampaignModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          {/* Platform Selection */}
          <div className="form-section">
            <h4>Select Platform</h4>
            <div className="platform-grid">
              {PLATFORMS.slice(0, 2).map(platform => (
                <div
                  key={platform.id}
                  className={`platform-option ${campaignForm.platform === platform.id ? 'selected' : ''}`}
                  onClick={() => setCampaignForm({ ...campaignForm, platform: platform.id })}
                  style={{ borderColor: campaignForm.platform === platform.id ? platform.color : 'var(--gray-200)' }}
                >
                  <span className="platform-icon" style={{ background: platform.color }}>{platform.icon}</span>
                  <span className="platform-name">{platform.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Campaign Type */}
          <div className="form-section">
            <h4>Campaign Type</h4>
            <div className="type-grid">
              {CAMPAIGN_TYPES[campaignForm.platform]?.map(type => (
                <label key={type.id} className={`type-option ${campaignForm.type === type.id ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="campaign-type"
                    value={type.id}
                    checked={campaignForm.type === type.id}
                    onChange={e => setCampaignForm({ ...campaignForm, type: e.target.value })}
                  />
                  <span className="type-icon">{type.icon}</span>
                  <span className="type-name">{type.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Basic Info */}
          <div className="form-section">
            <h4>Campaign Details</h4>
            <div className="form-group">
              <label>Campaign Name</label>
              <input
                type="text"
                className="form-control"
                value={campaignForm.name}
                onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })}
                placeholder="e.g., Summer Sale - Search Ads"
              />
            </div>
            <div className="form-group">
              <label>Campaign Objective</label>
              <textarea
                className="form-control"
                rows={2}
                value={campaignForm.objective}
                onChange={e => setCampaignForm({ ...campaignForm, objective: e.target.value })}
                placeholder="Describe your campaign goals..."
              />
            </div>
          </div>

          {/* Budget */}
          <div className="form-section">
            <h4>Budget & Schedule</h4>
            <div className="grid-2-col gap-4">
              <div className="form-group">
                <label>Daily Budget ($)</label>
                <input
                  type="number"
                  className="form-control"
                  value={campaignForm.budget_daily}
                  onChange={e => setCampaignForm({ ...campaignForm, budget_daily: e.target.value })}
                  placeholder="e.g., 100"
                />
              </div>
              <div className="form-group">
                <label>Total Budget ($)</label>
                <input
                  type="number"
                  className="form-control"
                  value={campaignForm.budget_total}
                  onChange={e => setCampaignForm({ ...campaignForm, budget_total: e.target.value })}
                  placeholder="e.g., 3000"
                />
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={campaignForm.start_date}
                  onChange={e => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>End Date (Optional)</label>
                <input
                  type="date"
                  className="form-control"
                  value={campaignForm.end_date}
                  onChange={e => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Bidding Strategy */}
          <div className="form-section">
            <h4>Bidding Strategy</h4>
            <div className="bidding-options">
              {BIDDING_STRATEGIES.map(strategy => (
                <label 
                  key={strategy.id}
                  className={`bidding-option ${campaignForm.bidding_strategy === strategy.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="bidding"
                    value={strategy.id}
                    checked={campaignForm.bidding_strategy === strategy.id}
                    onChange={e => setCampaignForm({ ...campaignForm, bidding_strategy: e.target.value })}
                  />
                  <div className="bidding-content">
                    <span className="bidding-name">{strategy.name}</span>
                    <span className="bidding-desc">{strategy.description}</span>
                  </div>
                </label>
              ))}
            </div>

            {(campaignForm.bidding_strategy === 'target-cpa' || campaignForm.bidding_strategy === 'target-roas') && (
              <div className="grid-2-col gap-4 mt-3">
                {campaignForm.bidding_strategy === 'target-cpa' && (
                  <div className="form-group">
                    <label>Target CPA ($)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={campaignForm.target_cpa}
                      onChange={e => setCampaignForm({ ...campaignForm, target_cpa: e.target.value })}
                      placeholder="e.g., 25"
                    />
                  </div>
                )}
                {campaignForm.bidding_strategy === 'target-roas' && (
                  <div className="form-group">
                    <label>Target ROAS (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={campaignForm.target_roas}
                      onChange={e => setCampaignForm({ ...campaignForm, target_roas: e.target.value })}
                      placeholder="e.g., 400"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Keywords (for Search campaigns) */}
          {campaignForm.type === 'search' && (
            <div className="form-section">
              <h4>Keywords</h4>
              <div className="form-group">
                <label>Target Keywords (one per line)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={campaignForm.targeting.keywords.join('\n')}
                  onChange={e => setCampaignForm({
                    ...campaignForm,
                    targeting: {
                      ...campaignForm.targeting,
                      keywords: e.target.value.split('\n').filter(k => k.trim())
                    }
                  })}
                  placeholder="Enter keywords, one per line..."
                />
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowCampaignModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleCreateCampaign}
            disabled={!campaignForm.name || !campaignForm.budget_daily}
          >
            🚀 Create Campaign
          </button>
        </div>
      </div>
    </div>
  );

  // Render optimize modal
  const renderOptimizeModal = () => (
    <div className="modal-overlay" onClick={() => setShowOptimizeModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🎯 Optimize Campaign</h3>
          <button className="btn-icon" onClick={() => setShowOptimizeModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="campaign-summary">
            <h4>{selectedCampaign?.name}</h4>
            <p className="text-secondary">AI-powered optimization recommendations</p>
          </div>

          <div className="recommendations">
            <div className="recommendation">
              <span className="rec-icon">💡</span>
              <div className="rec-content">
                <strong>Increase budget by 20%</strong>
                <p>Campaign is performing well with high ROAS. Increasing budget could yield more conversions.</p>
              </div>
              <button className="btn btn-sm btn-primary">Apply</button>
            </div>
            <div className="recommendation">
              <span className="rec-icon">🎯</span>
              <div className="rec-content">
                <strong>Add negative keywords</strong>
                <p>5 irrelevant search terms are consuming budget. Add them as negatives.</p>
              </div>
              <button className="btn btn-sm btn-primary">Apply</button>
            </div>
            <div className="recommendation">
              <span className="rec-icon">📊</span>
              <div className="rec-content">
                <strong>Expand audience targeting</strong>
                <p>Similar audiences show high engagement potential.</p>
              </div>
              <button className="btn btn-sm btn-primary">Apply</button>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowOptimizeModal(false)}>
            Close
          </button>
          <button className="btn btn-primary" onClick={handleOptimize}>
            ✨ Apply All Recommendations
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && !campaigns.length) {
    return (
      <div>
        <div className="loading-spinner">Loading Paid Advertising...</div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Paid Advertising</span>
      </nav>

      <div className="card">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1>💰 Paid Advertising</h1>
            <p className="text-secondary">Manage and optimize your paid advertising campaigns</p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCampaignModal(true)}
          >
            ➕ Create Campaign
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-danger mb-4">
            {error}
            <button className="btn-icon ml-auto" onClick={() => setError(null)}>✕</button>
          </div>
        )}
        {success && (
          <div className="alert alert-success mb-4">
            {success}
            <button className="btn-icon ml-auto" onClick={() => setSuccess(null)}>✕</button>
          </div>
        )}

        {/* Platform Tabs */}
        <div className="platform-tabs mb-4">
          {PLATFORMS.slice(0, 2).map(platform => (
            <button
              key={platform.id}
              className={`platform-tab ${activePlatform === platform.id ? 'active' : ''}`}
              onClick={() => setActivePlatform(platform.id)}
              style={{ 
                borderColor: activePlatform === platform.id ? platform.color : 'transparent',
                background: activePlatform === platform.id ? platform.color + '10' : 'transparent'
              }}
            >
              <span className="tab-icon" style={{ background: platform.color }}>{platform.icon}</span>
              <span>{platform.name}</span>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="tabs mb-4">
          {['overview', 'campaigns', 'performance', 'audiences'].map(tab => (
            <button
              key={tab}
              className={`tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' && '📊 Overview'}
              {tab === 'campaigns' && '📋 Campaigns'}
            {tab === 'performance' && '📈 Performance'}
            {tab === 'audiences' && '👥 Audiences'}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {renderStatsCards()}

          <div className="grid-2-col gap-4">
            <div className="card">
              <h3 className="card-title">Spend by Campaign</h3>
              <div className="spend-chart">
                {campaigns.slice(0, 5).map(campaign => (
                  <div key={campaign.id} className="spend-bar">
                    <span className="spend-name">{campaign.name}</span>
                    <div className="bar-container">
                      <div 
                        className="bar"
                        style={{ 
                          width: `${(campaign.spend / (budgetData?.total_spend || 1)) * 100}%`,
                          background: 'var(--primary-500)'
                        }}
                      />
                    </div>
                    <span className="spend-value">${campaign.spend?.toLocaleString() || '0'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="card-title">Top Performing Ads</h3>
              <div className="top-ads">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="top-ad">
                    <div className="ad-rank">#{i}</div>
                    <div className="ad-info">
                      <span className="ad-name">Ad Variation {i}</span>
                      <span className="ad-metrics">CTR: {(Math.random() * 5).toFixed(2)}% | Conv: {Math.floor(Math.random() * 100)}</span>
                    </div>
                    <div className="ad-score">
                      <span className="score">{Math.floor(Math.random() * 30) + 70}</span>
                      <span className="score-label">Score</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="campaigns-list">
          {campaigns.length > 0 ? (
            campaigns.map(renderCampaignCard)
          ) : (
            <div className="empty-state">
              <span style={{ fontSize: '4rem' }}>📢</span>
              <h3>No campaigns yet</h3>
              <p className="text-secondary">Create your first campaign to start advertising</p>
              <button className="btn btn-primary" onClick={() => setShowCampaignModal(true)}>
                ➕ Create Campaign
              </button>
            </div>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="grid-2-col gap-4">
          <div className="card col-span-2">
            <h3 className="card-title">Performance Over Time</h3>
            <div className="chart-placeholder" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="text-secondary">📈 Performance chart would display here</span>
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Key Metrics</h3>
            <div className="metrics-list">
              {[
                { name: 'Cost per Click (CPC)', value: '$1.25', change: '-5%', positive: true },
                { name: 'Cost per Acquisition (CPA)', value: '$28.50', change: '-12%', positive: true },
                { name: 'Click-Through Rate (CTR)', value: '3.2%', change: '+8%', positive: true },
                { name: 'Conversion Rate', value: '4.5%', change: '+15%', positive: true },
                { name: 'Quality Score (Avg)', value: '8.2/10', change: '+0.5', positive: true }
              ].map(metric => (
                <div key={metric.name} className="metric-row">
                  <span className="metric-name">{metric.name}</span>
                  <span className="metric-value">{metric.value}</span>
                  <span className={`metric-change ${metric.positive ? 'positive' : 'negative'}`}>
                    {metric.change}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Budget Allocation</h3>
            <div className="budget-allocation">
              {PLATFORMS.slice(0, 2).map(platform => (
                <div key={platform.id} className="budget-item">
                  <div className="budget-platform">
                    <span className="platform-icon-sm" style={{ background: platform.color }}>{platform.icon}</span>
                    <span>{platform.name}</span>
                  </div>
                  <div className="budget-bar-container">
                    <div 
                      className="budget-bar"
                      style={{ 
                        width: `${platform.id === 'google' ? 60 : 40}%`,
                        background: platform.color
                      }}
                    />
                  </div>
                  <span className="budget-amount">${platform.id === 'google' ? '6,000' : '4,000'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Audiences Tab */}
      {activeTab === 'audiences' && (
        <div className="card">
          <div className="flex justify-between align-center mb-4">
            <h3 className="card-title mb-0">Saved Audiences</h3>
            <button className="btn btn-primary">➕ Create Audience</button>
          </div>
          
          <div className="audiences-grid">
            {[
              { name: 'High-Value Customers', size: '125K', platform: 'meta' },
              { name: 'Website Visitors - 30 days', size: '450K', platform: 'google' },
              { name: 'Cart Abandoners', size: '85K', platform: 'meta' },
              { name: 'Newsletter Subscribers', size: '200K', platform: 'google' },
              { name: 'Lookalike - Top Buyers', size: '2.1M', platform: 'meta' }
            ].map((audience, idx) => {
              const platform = PLATFORMS.find(p => p.id === audience.platform);
              return (
                <div key={idx} className="audience-card">
                  <div className="audience-header">
                    <span className="platform-icon-sm" style={{ background: platform?.color }}>{platform?.icon}</span>
                    <h4>{audience.name}</h4>
                  </div>
                  <div className="audience-size">
                    <span className="size-value">{audience.size}</span>
                    <span className="size-label">people</span>
                  </div>
                  <div className="audience-actions">
                    <button className="btn btn-sm btn-secondary">Edit</button>
                    <button className="btn btn-sm btn-primary">Use</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modals */}
      {showCampaignModal && renderCampaignModal()}
      {showOptimizeModal && renderOptimizeModal()}

      <style>{`
        .tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--gray-200);
        }
        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          color: var(--gray-600);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
        }
        .tab.active {
          color: var(--primary-600);
          border-bottom-color: var(--primary-600);
          font-weight: 600;
        }
        .platform-tabs {
          display: flex;
          gap: 1rem;
        }
        .platform-tab {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          border: 2px solid transparent;
          border-radius: var(--radius-lg);
          background: none;
          cursor: pointer;
          transition: all 0.2s;
        }
        .platform-tab .tab-icon {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        .stat-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: white;
          padding: 1.5rem;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
        }
        .stat-icon {
          font-size: 2rem;
        }
        .stat-trend {
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }
        .stat-trend.success {
          color: var(--success-600);
        }
        .campaigns-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .campaign-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
        }
        .campaign-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .campaign-platform {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          color: white;
        }
        .campaign-info {
          flex: 1;
        }
        .campaign-name {
          margin: 0;
          font-size: 1rem;
        }
        .campaign-type {
          font-size: 0.8rem;
          color: var(--gray-500);
          text-transform: capitalize;
        }
        .campaign-status {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          color: white;
          text-transform: uppercase;
        }
        .campaign-metrics {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 1rem;
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
        }
        .metric {
          text-align: center;
        }
        .metric-value {
          display: block;
          font-weight: 600;
          font-size: 1rem;
        }
        .metric-label {
          font-size: 0.7rem;
          color: var(--gray-500);
        }
        .campaign-budget {
          margin-bottom: 1rem;
        }
        .budget-info {
          display: flex;
          justify-content: space-between;
          font-size: 0.8rem;
          margin-bottom: 0.25rem;
        }
        .progress-bar {
          height: 8px;
          background: var(--gray-200);
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
        }
        .campaign-actions {
          display: flex;
          gap: 0.5rem;
        }
        .form-section {
          margin-bottom: 1.5rem;
        }
        .form-section h4 {
          margin-bottom: 1rem;
          color: var(--gray-700);
        }
        .platform-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }
        .platform-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border: 2px solid var(--gray-200);
          border-radius: var(--radius-lg);
          cursor: pointer;
          transition: all 0.2s;
        }
        .platform-option.selected {
          background: var(--gray-50);
        }
        .platform-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }
        .type-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.75rem;
        }
        .type-option {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1rem;
          border: 2px solid var(--gray-200);
          border-radius: var(--radius-sm);
          cursor: pointer;
        }
        .type-option.selected {
          border-color: var(--primary-500);
          background: var(--primary-50);
        }
        .type-option input {
          display: none;
        }
        .type-icon {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .type-name {
          font-size: 0.8rem;
        }
        .bidding-options {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .bidding-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border: 2px solid var(--gray-200);
          border-radius: var(--radius-sm);
          cursor: pointer;
        }
        .bidding-option.selected {
          border-color: var(--primary-500);
          background: var(--primary-50);
        }
        .bidding-content {
          display: flex;
          flex-direction: column;
        }
        .bidding-name {
          font-weight: 600;
        }
        .bidding-desc {
          font-size: 0.8rem;
          color: var(--gray-500);
        }
        .recommendations {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .recommendation {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .rec-icon {
          font-size: 1.5rem;
        }
        .rec-content {
          flex: 1;
        }
        .rec-content p {
          font-size: 0.8rem;
          color: var(--gray-500);
          margin: 0;
        }
        .spend-chart, .metrics-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .spend-bar {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .spend-name {
          width: 150px;
          font-size: 0.875rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bar-container {
          flex: 1;
          height: 20px;
          background: var(--gray-200);
          border-radius: 10px;
          overflow: hidden;
        }
        .bar {
          height: 100%;
          border-radius: 10px;
        }
        .spend-value {
          width: 80px;
          text-align: right;
          font-weight: 600;
        }
        .metric-row {
          display: flex;
          align-items: center;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .metric-name {
          flex: 1;
          font-size: 0.875rem;
        }
        .metric-change {
          font-size: 0.8rem;
          font-weight: 600;
        }
        .metric-change.positive {
          color: var(--success-600);
        }
        .metric-change.negative {
          color: var(--primary-500);
        }
        .top-ads {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .top-ad {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .ad-rank {
          font-weight: 600;
          color: var(--primary-600);
        }
        .ad-info {
          flex: 1;
        }
        .ad-name {
          display: block;
          font-weight: 500;
        }
        .ad-metrics {
          font-size: 0.75rem;
          color: var(--gray-500);
        }
        .ad-score {
          text-align: center;
        }
        .score {
          display: block;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--success-600);
        }
        .score-label {
          font-size: 0.7rem;
          color: var(--gray-500);
        }
        .audiences-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1rem;
        }
        .audience-card {
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-lg);
        }
        .audience-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .audience-header h4 {
          margin: 0;
          font-size: 0.9rem;
        }
        .platform-icon-sm {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.75rem;
        }
        .audience-size {
          text-align: center;
          margin-bottom: 1rem;
        }
        .size-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .size-label {
          font-size: 0.75rem;
          color: var(--gray-500);
        }
        .audience-actions {
          display: flex;
          gap: 0.5rem;
        }
        .col-span-2 {
          grid-column: span 2;
        }
        .empty-state {
          text-align: center;
          padding: 4rem;
          background: var(--gray-50);
          border-radius: var(--radius-lg);
        }
        .empty-state h3 {
          margin: 1rem 0 0.5rem;
        }
      `}</style>
      </div>
    </div>
  );
};

export default PaidAds;
